import { useId, useState } from "react";
import { Link } from "react-router";
import type {
  CommandLink,
  ManifestListFilter,
  ManifestObject,
} from "@shared-core/domain/object-ui/contract";
import { useTimeline } from "@shared-core/domain/object-ui/hooks";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import {
  emptyTimelineSearch,
  hasMoreTimeline,
  isTimelineNarrowed,
  type TimelineSearch,
} from "@shared-core/domain/object-ui/timeline";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { FileLinks } from "../../app/components/patterns/FileLinks";
import { Button } from "../../app/components/primitives/button";
import { Card, CardContent } from "../../app/components/primitives/card";
import { Input } from "../../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import { Skeleton } from "../../app/components/primitives/skeleton";
import { useIsPhone } from "../../app/useIsPhone";

const ANY = "__any__";

/**
 * The record's timeline card, the same for every object with a timeline: its
 * first page, "Show more" for the next, and, where the timeline declares them,
 * a search box (with what the search covers under it) and the timeline's
 * filters. Searching and filtering run on the server; a change starts again at
 * the first page.
 */
export function RecordTimeline({
  object,
  recordKey,
}: {
  object: ManifestObject;
  recordKey: string;
}) {
  const index = useObjectManifest();
  const record = object.record!;
  const searchDescription = record.timeline_search ?? null;
  const filters = record.timeline_filters ?? [];
  const [text, setText] = useState("");
  const [picked, setPicked] = useState<Record<string, string>>({});
  const q = useDebouncedValue(text, 300);
  const search: TimelineSearch = {
    ...emptyTimelineSearch(),
    q,
    filters: picked,
  };
  const timeline = useTimeline(object, recordKey, search);
  const describedBy = useId();
  const isPhone = useIsPhone();
  const pick = (param: string, value: string) =>
    setPicked((current) => ({ ...current, [param]: value }));

  return (
    <Card>
      <CardContent className="space-y-2 py-4 max-sm:px-4">
        <h2 className="text-base font-semibold">Timeline</h2>
        {(searchDescription !== null || filters.length > 0) && (
          <div className="space-y-2">
            {searchDescription !== null && (
              <div className="space-y-1">
                <Input
                  type="search"
                  aria-label="Search the timeline"
                  aria-describedby={describedBy}
                  placeholder="Search the timeline"
                  className={isPhone ? "h-10" : "h-9"}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
                <p id={describedBy} className="text-xs text-muted-foreground">
                  {searchDescription}
                </p>
              </div>
            )}
            {filters.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {filters.map((filter) => (
                  <TimelineFilter
                    key={filter.param}
                    filter={filter}
                    value={picked[filter.param] ?? ""}
                    onChange={(value) => pick(filter.param, value)}
                    isPhone={isPhone}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {timeline.status === "loading" && <Skeleton className="h-16 w-full" />}
        {timeline.status === "error" && (
          <p className="text-sm text-muted-foreground">
            Couldn't load the timeline.
          </p>
        )}
        {timeline.status === "ready" &&
          (timeline.shown.events.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {isTimelineNarrowed(search)
                ? "No timeline events match the search."
                : "No events yet."}
            </p>
          ) : (
            <>
              <ul className="space-y-2.5 sm:space-y-1.5">
                {timeline.shown.events.map((event, n) => (
                  <li
                    key={`${n}-${event.at}`}
                    className="grid gap-x-2 text-sm sm:grid-cols-[8.5rem_minmax(0,1fr)]"
                  >
                    <span className="tabular-nums text-muted-foreground">
                      {formatDateTimeOrDash(event.at)}
                    </span>
                    <span className="min-w-0 break-words">
                      <TimelineSummary
                        summary={event.summary}
                        href={event.record ? index.hrefOf(event.record) : null}
                      />
                      {event.actor && (
                        <span className="text-muted-foreground">
                          {" "}
                          · {event.actor}
                        </span>
                      )}
                      {event.links && event.links.length > 0 && (
                        <TimelineLinks links={event.links} />
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {hasMoreTimeline(timeline.shown) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="max-sm:h-10"
                  disabled={timeline.isLoadingMore}
                  onClick={timeline.showMore}
                >
                  Show more
                </Button>
              )}
              {timeline.hasMoreFailed && (
                <p role="alert" className="text-sm text-red-600">
                  Couldn't load more of the timeline.
                </p>
              )}
            </>
          ))}
      </CardContent>
    </Card>
  );
}

/** One of the timeline's own filters: a choice of values, or a day. */
function TimelineFilter({
  filter,
  value,
  onChange,
  isPhone,
}: {
  filter: ManifestListFilter;
  value: string;
  onChange: (value: string) => void;
  isPhone: boolean;
}) {
  if (filter.kind === "enum" || filter.kind === "multi_enum")
    return (
      <Select
        value={value === "" ? ANY : value}
        onValueChange={(next) => onChange(next === ANY ? "" : next)}
      >
        <SelectTrigger
          aria-label={filter.label}
          className={isPhone ? "h-10 w-full" : "h-9 w-auto min-w-36"}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{filter.label}: any</SelectItem>
          {filter.options.map(([option, label]) => (
            <SelectItem key={option} value={option}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  if (filter.kind === "date")
    return (
      <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
        {filter.label}
        <Input
          type="date"
          className={isPhone ? "h-10 w-auto" : "h-9 w-auto"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  return null;
}

/** A timeline event's summary: a link to the record it opens, when it opens one. */
function TimelineSummary({
  summary,
  href,
}: {
  summary: string;
  href: string | null;
}) {
  return href ? (
    <Link to={href} className="text-primary underline-offset-2 hover:underline">
      {summary}
    </Link>
  ) : (
    <>{summary}</>
  );
}

/**
 * The links a timeline event carries (a comment's files). An API path is
 * fetched with the person's sign-in (`FileLinks`), since a plain link would
 * reach the API without it; any other link opens as it stands.
 */
function TimelineLinks({ links }: { links: CommandLink[] }) {
  return (
    <span className="mt-0.5 flex flex-col items-start gap-0.5">
      {links.map((link) =>
        // An API path is a file behind the person's sign-in, opened as a record's file fact is; anything else is a page.
        link.href.startsWith("/") ? (
          <FileLinks
            key={link.href}
            files={[{ name: link.label, href: link.href, size_bytes: null }]}
          />
        ) : (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {link.label}
          </a>
        ),
      )}
    </span>
  );
}
